import axios from 'axios';
import { Config } from 'config/Config';
import { getAccessToken } from 'lib/Helper/Auth';
import {
  CreateTermsRequest,
  CreateTermsResponse,
  FetchTermsParams,
  FetchTermsResponse,
  UpdateTermsDetailsRequest,
  UpdateTermsDetailsResponse,
  TermsChangelogItem,
  HierarchyCompany,
  CreateConsolidatedTermsRequest,
  CreateConsolidatedTermsResponse,
  ConsolidatedGroupTerm,
} from 'lib/Model/TermsGenerator';

export async function createTerms(
  request: CreateTermsRequest,
): Promise<CreateTermsResponse> {
  const response = await axios.post(
    `${Config.API_BASE_URL}intranet/terms_of_business/generate`,
    request,
    {
      headers: {
        'Content-Type': 'application/json',
        'access-token': getAccessToken(),
      },
    },
  );

  return response.data;
}

export async function fetchTerms(
  params: FetchTermsParams = {},
): Promise<FetchTermsResponse> {
  const {
    page = 1,
    pageSize = 100,
    searchParam,
    companyId,
    status,
    brand,
  } = params;

  let url = `${Config.API_BASE_URL}intranet/terms_of_business/terms`;

  if (companyId) {
    url = `${Config.API_BASE_URL}intranet/companies/${companyId}/terms`;
  }

  const queryParams: Record<string, any> = {
    page: page,
    page_size: pageSize,
  };

  if (searchParam) {
    queryParams.search_param = searchParam;
  }
  if (status) {
    queryParams.status = status;
  }
  if (brand) {
    queryParams.brand = brand;
  }
  if (params.country) {
    queryParams.country = params.country;
  }

  const response = await axios.get(url, {
    params: queryParams,
    headers: {
      'Content-Type': 'application/json',
      'access-token': getAccessToken(),
    },
  });

  return response.data;
}

export async function updateTermsCompany(
  termsId: number,
  companyId: string,
): Promise<{ message: string; terms_id: number }> {
  const response = await axios.put(
    `${Config.API_BASE_URL}intranet/terms_of_business/terms/${termsId}`,
    { companyId },
    {
      headers: {
        'Content-Type': 'application/json',
        'access-token': getAccessToken(),
      },
    },
  );

  return response.data;
}

export async function updateTermsDetails(
  termsId: number,
  request: UpdateTermsDetailsRequest,
): Promise<UpdateTermsDetailsResponse> {
  const response = await axios.patch(
    `${Config.API_BASE_URL}intranet/terms_of_business/terms/${termsId}/details`,
    request,
    {
      headers: {
        'Content-Type': 'application/json',
        'access-token': getAccessToken(),
      },
    },
  );

  return response.data;
}

export async function fetchTermsChangelog(
  termsId: number,
): Promise<TermsChangelogItem[]> {
  const response = await axios.get(
    `${Config.API_BASE_URL}intranet/terms_of_business/terms/${termsId}/changelog`,
    {
      headers: {
        'Content-Type': 'application/json',
        'access-token': getAccessToken(),
      },
    },
  );

  return response.data?.data ?? response.data;
}

export async function fetchCompanyHierarchy(
  companyId: string,
): Promise<HierarchyCompany[]> {
  const response = await axios.get(
    `${Config.API_BASE_URL}intranet/companies/${companyId}/hierarchy`,
    {
      headers: {
        'Content-Type': 'application/json',
        'access-token': getAccessToken(),
      },
    },
  );

  return response.data?.data ?? [];
}

export async function createConsolidatedTerms(
  request: CreateConsolidatedTermsRequest,
): Promise<CreateConsolidatedTermsResponse> {
  const response = await axios.post(
    `${Config.API_BASE_URL}intranet/terms_of_business/generate_consolidated`,
    request,
    {
      headers: {
        'Content-Type': 'application/json',
        'access-token': getAccessToken(),
      },
    },
  );

  return response.data;
}

export async function fetchConsolidatedGroupTerms(
  groupId: number,
): Promise<ConsolidatedGroupTerm[]> {
  const response = await axios.get(
    `${Config.API_BASE_URL}intranet/terms_of_business/consolidated_group/${groupId}`,
    {
      headers: {
        'Content-Type': 'application/json',
        'access-token': getAccessToken(),
      },
    },
  );
  return response.data?.data ?? [];
}

export async function updateConsolidatedGroupCancellation(
  groupId: number,
  isCancelled: boolean,
): Promise<{ message: string }> {
  const response = await axios.patch(
    `${Config.API_BASE_URL}intranet/terms_of_business/consolidated_group/${groupId}/cancellation`,
    { isCancelled },
    {
      headers: {
        'Content-Type': 'application/json',
        'access-token': getAccessToken(),
      },
    },
  );
  return response.data;
}
